import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Icon,
  Button,
} from '@mui/material';
import { AiOutlineArrowDown, AiOutlineArrowUp } from 'react-icons/ai';
import moment from 'moment';
import 'moment/locale/de';
import {
  GET_ALL_INVOICES_COUNT,
  GET_INVOICES_OFFSET,
} from '../queries/adminQueries';
import { useQuery } from '@apollo/client';

/**
 * InvoicesView
 */
const InvoicesView = (props: any) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState<string>('desc');

  // Die Anzahl der Einträge ermitteln.
  const {
    loading: countLoading,
    error: countError,
    data: countData,
    refetch: countRefetch,
  } = useQuery(GET_ALL_INVOICES_COUNT, {
    fetchPolicy: 'network-only',
    pollInterval: 3000, // <-- refetch every 3 sec
  });

  // Die Einträge der einzelnen Offsets holen.
  const {
    loading: offsetLoading,
    error: offsetError,
    data: offsetData,
    refetch: offsetRefetch,
    fetchMore,
  } = useQuery(GET_INVOICES_OFFSET, {
    variables: {
      offset: page * pageSize,
      limit: pageSize,
      sortOrder: sortOrder,
    },
    fetchPolicy: 'network-only',
    pollInterval: 3000, // <-- refetch every 3 sec
  });

  moment().locale('de');

  /**
   * Falls die Navigation vom Customer kommt.
   */
  useEffect(() => {
    if (Object.keys(props.childPagePropsFromParent).length !== 0) {
      setPage(props.childPagePropsFromParent.page);
      setPageSize(props.childPagePropsFromParent.pageSize);
      setSortOrder(props.childPagePropsFromParent.sortOrder);
    }
  }, [props.childPagePropsFromParent]);

  /**
   * Funktion um den Offset zu setzen.
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchMore({
      variables: {
        offset: newPage * pageSize,
        limit: pageSize,
        sortOrder,
      },
    });
  };

  /**
   * Funktion um pageSize zu ändern.
   */
  const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(+event.target.value);
    setPage(0);
  };

  /**
   * Handle sortOrder
   */
  const handleSortOrder = () => {
    if (sortOrder === 'asc') {
      setSortOrder('desc');
    } else {
      setSortOrder('asc');
    }
  };

  // Beim laden der Daten nichts anzeigen.
  if (countLoading) {
    return null;
  }

  // Falls Fehler bei der Abfrage auftreten.
  if (countError) {
    console.log('Error! InvoicesView: ' + countError.message);
  }

  // Beim laden der Daten nichts anzeigen.
  if (offsetLoading) {
    return null;
  }

  // Falls Fehler bei der Abfrage auftreten.
  if (offsetError) {
    console.log('Error! InvoicesView: ' + offsetError.message);
  }

  // return
  return (
    <Container maxWidth='lg'>
      <Box>
        <Typography component='h2' variant='h6' align='center' mt={2}>
          Rechnungen
        </Typography>
      </Box>
      <Box sx={{ marginTop: 2, width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader aria-label='sticky table' size='small'>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    width: 130,
                    maxWidth: 130,
                  }}
                  onClick={handleSortOrder}
                >
                  RechnungsNr.
                  {sortOrder === 'asc' ? (
                    <Icon>
                      <AiOutlineArrowUp size='16' />
                    </Icon>
                  ) : (
                    <Icon>
                      <AiOutlineArrowDown size='16' />
                    </Icon>
                  )}
                </TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>Status</TableCell>
                <TableCell>Geschäft</TableCell>
                <TableCell sx={{ width: 60, maxWidth: 60 }}>PLZ</TableCell>
                <TableCell>Ort</TableCell>
                <TableCell sx={{ width: 120, maxWidth: 120 }}>
                  Service
                </TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>Preis</TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>Erstellt</TableCell>
              </TableRow>
            </TableHead>
            {offsetData !== undefined && offsetData.invoice.length > 0 ? (
              <TableBody>
                {offsetData.invoice.map((invoice: any) => (
                  <TableRow
                    sx={{
                      backgroundColor:
                        invoice.status === 'open'
                          ? '#FF3D00'
                          : invoice.status === 'posted'
                          ? '#FF6E40' 
                          : undefined,
                    }}
                    key={invoice.id}
                    hover
                    onClick={() => {
                      props.sendChildPagePropsToParent({
                        id: invoice.id,
                        store_id: invoice.store_id,
                        status: invoice.status,
                        storeowner: invoice.storeservice.store.storeowner,
                        storename: invoice.storeservice.store.storename,
                        street: invoice.storeservice.store.street,
                        zipcode: invoice.storeservice.store.location.zipcode,
                        place: invoice.storeservice.store.location.place,
                        tenant_id: invoice.storeservice.store.tenant.id,
                        invoiceemail:
                          invoice.storeservice.store.tenant.invoiceemail,
                        servicename:
                          invoice.storeservice.servicetype.servicename,
                        serviceprice: invoice.serviceprice,
                        invoicedate: invoice.invoicedate,
                        storestatus: invoice.storeservice.store.storestatus,
                        page,
                        pageSize,
                        sortOrder,
                      });
                      props.sendChildPageToParent('invoice');
                    }}
                  >
                    <TableCell sx={{ width: 130, maxWidth: 130 }}>
                      {invoice.id}
                    </TableCell>
                    <TableCell>
                      {invoice.status === 'open'
                        ? 'offen'
                        : invoice.status === 'posted'
                        ? 'gesendet'
                        : 'bezahlt'}
                    </TableCell>
                    <TableCell>
                      {invoice.storeservice.store.storename}
                    </TableCell>
                    <TableCell>
                      {invoice.storeservice.store.location.zipcode}
                    </TableCell>
                    <TableCell>
                      {invoice.storeservice.store.location.place}
                    </TableCell>
                    <TableCell>
                      {invoice.storeservice.servicetype.servicename}
                    </TableCell>
                    <TableCell>
                      {parseFloat(invoice.serviceprice)
                        .toFixed(2)
                        .replace('.', ',')}{' '}
                      €
                    </TableCell>
                    <TableCell>
                      {moment(invoice.invoicedate).format('L')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <Typography variant='subtitle1' align='center'>
                Keine vorhanden!
              </Typography>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          labelRowsPerPage={'Zeilen:'}
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={countData.invoice_aggregate.aggregate.totalCount}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangePageSize}
        />
      </Box>
      <Box
        mt={3}
        mb={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Button
          sx={{
            borderRadius: 30,
            width: '240px',
          }}
          type='submit'
          variant='contained'
          color='secondary'
          onClick={() => {
            countRefetch();
            offsetRefetch();
          }}
        >
          Aktualisieren
        </Button>
      </Box>
    </Container>
  );
};

export default InvoicesView;
